import { useEffect, useState } from 'react';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/lib/supabase';
import { captureError } from '@/lib/sentry';
import { Loader2 } from 'lucide-react';

interface MailingAddressRequiredDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workspaceId: string;
  // Called after the address is saved successfully. The wizard re-runs
  // handleScheduleSend from here so the activation flow continues
  // transparently without losing wizard state.
  onSaved: () => void;
  // Called when the user clicks Cancel — wizard stays on step 4.
  onCancel: () => void;
}

/**
 * Inline AlertDialog surfaced at the campaign activation moment when the
 * workspace has no CAN-SPAM mailing address set. Replaces the destructive
 * toast that previously sent the user away to Settings (losing wizard state)
 * and noisily logged a user-correctable error to GlitchTip.
 *
 * Persists to workspace_settings.compliance_physical_address (the workspace-
 * level CAN-SPAM fallback that satisfies every sender, mirroring the queue
 * processor's send-time resolution order). The per-sender override in
 * email_providers.physical_address still wins when set — this just
 * guarantees a baseline.
 *
 * Schema deliberately matches SenderSettingsDialog (a single free-form text
 * field). The compliance footer renders newlines as <br>, so users can break
 * the address across visual lines.
 */
export function MailingAddressRequiredDialog({
  open,
  onOpenChange,
  workspaceId,
  onSaved,
  onCancel,
}: MailingAddressRequiredDialogProps) {
  const { toast } = useToast();
  const [address, setAddress] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  // Reset the input each time the dialog opens so prior aborted attempts
  // don't leak across opens.
  useEffect(() => {
    if (open) {
      setAddress('');
      setIsSaving(false);
    }
  }, [open]);

  const trimmed = address.trim();
  const canSave = trimmed.length > 0 && !isSaving;

  const handleSave = async () => {
    if (!trimmed || !workspaceId) return;
    setIsSaving(true);
    try {
      // workspace_settings is upserted on workspace_id; RLS restricts modify
      // to owner/admin. The zero-rows-returned shape is a permission failure
      // (mirrors useWorkspaceSettings.updateSettings).
      const { data, error } = await supabase
        .from('workspace_settings')
        .upsert(
          {
            workspace_id: workspaceId,
            compliance_physical_address: trimmed,
          },
          { onConflict: 'workspace_id' },
        )
        .select('workspace_id');
      if (error) throw error;
      if (!data || data.length === 0) {
        throw new Error(
          'You do not have permission to set the workspace mailing address. Ask a workspace owner or admin.',
        );
      }
      onSaved();
    } catch (err) {
      console.error('Failed to save workspace mailing address:', err);
      captureError(err as Error, { feature: 'mailing-address-required-dialog' });
      toast({
        title: 'Could not save address',
        description: err instanceof Error ? err.message : 'Failed to save mailing address.',
        variant: 'destructive',
      });
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    if (isSaving) return;
    onCancel();
  };

  return (
    <AlertDialog
      open={open}
      onOpenChange={(next) => {
        // Dismissing the dialog (Esc, outside click) is equivalent to cancel.
        if (!next && !isSaving) {
          onCancel();
        }
        onOpenChange(next);
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Add a mailing address to send this campaign</AlertDialogTitle>
          <AlertDialogDescription>
            The US CAN-SPAM Act requires a physical mailing address in marketing emails. Add one for
            this workspace and we'll continue activating your campaign — no need to leave the
            wizard.
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-2 py-2">
          <Label htmlFor="workspace-mailing-address">Workspace mailing address</Label>
          <Textarea
            id="workspace-mailing-address"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            placeholder="e.g. Acme Inc, 123 Main St, Springfield, IL 62704"
            rows={3}
            autoFocus
            disabled={isSaving}
          />
          <p className="text-xs text-muted-foreground">
            Appended to every Hermes campaign sent from this workspace, above the unsubscribe link.
            A per-sender address (in Sender Settings) overrides this when set.
          </p>
        </div>

        <AlertDialogFooter>
          <Button variant="outline" onClick={handleCancel} disabled={isSaving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={!canSave}>
            {isSaving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              'Save & continue'
            )}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
